import React, { useCallback, useContext, useState } from 'react';
import classNames from 'classnames';
import {
  DataRoomMember,
  DataRoomMembers,
} from 'router/subrouters/DataRoom/models/DataRoomMember';
import { DocumentsAvailabilityDrawer } from 'router/subrouters/DataRoom/components/DocumentAvailabilityDrawer';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';
import { ManageRequests } from './components/ManageRequests';
import DataRoomMembersTable from 'router/subrouters/DataRoom/components/DataRoomMembersTable';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { getHasElementsInArray } from 'utils';

import './MembersTable.styles.scss';
import './MembersTable.styles.responsive.scss';

type MembersTableProps = {
  className?: string;
  isBuySide?: boolean;
  showDiscoveryFeature?: boolean;
  members: DataRoomMember[];
};

const MembersTable: React.FC<MembersTableProps> = (props) => {
  const {
    className,
    members,
    showDiscoveryFeature = false,
    isBuySide = false,
  } = props;

  const [selectedMember, setSelectedMember] = useState<DataRoomMember>();
  const [manageDocuments, manageDocumentsRef] =
    useCallbackRef<SideDrawerRef>(null);
  const [manageRequests, manageRequestsRef] =
    useCallbackRef<SideDrawerRef>(null);

  const {
    setBuySideGroups,
    setSellSideMembers,
    updateMember: updateMemberContext,
  } = useContext(MembersContext);

  const classes = classNames('members-table', className);

  const updateDataRoomMembers = useCallback(
    ({ buySideGroups, sellSideMembers }: DataRoomMembers) => {
      setBuySideGroups(buySideGroups);
      setSellSideMembers(sellSideMembers);
    },
    [setBuySideGroups, setSellSideMembers],
  );

  const handleOnEdit = useCallback(
    (member: DataRoomMember) => {
      setSelectedMember(member);

      if (member?.requestedDataRoomAccess) manageRequests?.setOpen(true);
      else manageDocuments?.setOpen(true);
    },
    [manageDocuments, manageRequests],
  );

  const updateMember = useCallback(
    (updatedMember: Partial<DataRoomMember>) => {
      setSelectedMember((old) => ({ ...old, ...updatedMember }));

      updateMemberContext(updatedMember);
    },
    [updateMemberContext],
  );

  const removeMember = useCallback(
    (memberId: string) => {
      setSellSideMembers((old) => old.filter(({ _id }) => _id !== memberId));
      setSelectedMember(null);
    },
    [setSellSideMembers],
  );

  if (!getHasElementsInArray(members)) return null;

  return (
    <>
      <DataRoomMembersTable
        showChat
        isBuySide={isBuySide}
        className={classes}
        members={members}
        showDiscoveryFeature={showDiscoveryFeature}
        onEdit={handleOnEdit}
        onEditMemberRole={(memberId, updated) => {
          dataRoomApi.members.updateMember(memberId, updated);
          updateMember({ _id: memberId, ...updated });
        }}
      />

      <DocumentsAvailabilityDrawer
        ref={manageDocumentsRef}
        isBuySide={isBuySide}
        member={selectedMember}
        defaultOpenRoles
        hasAdditionalActions
        updateMember={updateMember}
        updateDataRoomMembers={updateDataRoomMembers}
      />

      <ManageRequests
        ref={manageRequestsRef}
        isBuySide={isBuySide}
        member={selectedMember}
        updateMember={updateMember}
        removeMember={removeMember}
      />
    </>
  );
};

export default MembersTable;
